// Provenance: repo:tds-reconciliation-portal:frontend/src/components/DifferenceView.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// 20-column profile, memoised rows, sticky columns, one-band controls and the
// match/explain/alias selection logic carry unchanged. Un-match travels by
// `match_resolution_id` — the predecessor negated its integer group id, which a UUID cannot.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Filter, Link2, MessageSquare, Search, Tags } from 'lucide-react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, postJson } from '../api/client'
import { useDifference, useDifferenceParties, useRun } from '../api/hooks'
import type { DifferenceItem, DiffTotals } from '../api/types'
import { stepUpPost } from '../lib/stepup'
import { useDebounced } from '../lib/useDebounced'
import { Chip, type Tone } from './badges'
import { ChipStrip, StatChip } from './ChipStrip'
import { ExplainModal } from './ExplainModal'
import type { ExplainEntry } from './ExplainModal'
import { Btn } from './Modal'
import { Money } from './Money'
import { TablePager } from './TablePager'
import { showToast } from './toast'

const fmt = new Intl.NumberFormat('en-IN')

const STATUS_META: Record<string, { label: string; tone: Tone; solid: boolean }> = {
  ALL_MATCH: { label: 'All match', tone: 'green', solid: false },
  USER_MATCH: { label: 'User match', tone: 'violet', solid: true },
  DIFF: { label: 'Difference', tone: 'red', solid: true },
  ONLY_26AS: { label: 'Only 26AS', tone: 'orange', solid: true },
  ONLY_BOOKS: { label: 'Only books', tone: 'yellow', solid: true },
}
const DATE_TONE: Record<string, Tone> = { OK: 'green', OUT_OF_WINDOW: 'orange', NA: 'gray' }
const PARTY_META: Record<string, { label: string; tone: Tone }> = {
  OK: { label: 'OK', tone: 'green' },
  TAN_MATCH_NAME_DIFF: { label: 'TAN✓ name≠', tone: 'blue' },
  NAME_MISMATCH: { label: 'name~', tone: 'orange' },
}

// exact column profile requested (Sr.No … Status), widths in px
const COLS: [string, number][] = [
  ['Sr.', 46],
  ['Party (26AS)', 200],
  ['PAN', 88],
  ['TAN', 88],
  ['Sec', 56],
  ['Date 26AS', 92],
  ['Amt 26AS', 104],
  ['TDS 26AS', 104],
  ['Party (Books)', 190],
  ['TAN Books', 88],
  ['Sec Bk', 56],
  ['Date Books', 92],
  ['Amt Books', 96],
  ['TDS Books', 104],
  ['Date flag', 92],
  ['Party flag', 108],
  ['Sec flag', 74],
  ['Amt Diff', 100],
  ['TDS Diff', 104],
  ['Status', 104],
]
const MIN_WIDTH = COLS.reduce((a, [, w]) => a + w, 0)

// cumulative left offsets for the sticky columns — COLS is a constant, so this
// is too; computing it per render (let alone per row) bought nothing
const LEFT_OF = COLS.map((_, i) => COLS.slice(0, i).reduce((a, [, w]) => a + w, 0))

const stickyStyle = (i: number): React.CSSProperties | undefined =>
  i < 2 ? { position: 'sticky', left: LEFT_OF[i], zIndex: 2 } : undefined

/** All / Matched / Unmatched, one grid. In the predecessor these were two tabs
 *  plus a separate read-only page rendering this same component. */
const VIEWS = [
  { value: 'all', label: 'All' },
  { value: 'matched', label: 'Matched' },
  { value: 'unmatched', label: 'Unmatched' },
] as const
type View = (typeof VIEWS)[number]['value']

//: which statuses can occur in each view — offering ONLY_26AS while the grid
//: shows matched rows is a filter that can only ever return nothing
export const STATUSES_FOR: Record<View, string[]> = {
  all: ['ALL_MATCH', 'USER_MATCH', 'DIFF', 'ONLY_26AS', 'ONLY_BOOKS'],
  matched: ['ALL_MATCH', 'USER_MATCH', 'DIFF'],
  unmatched: ['ONLY_26AS', 'ONLY_BOOKS'],
}

//: a row the accountant can still act on: it is neither an agreed pair nor
//: already dispositioned
export const OPEN_STATUSES = ['ONLY_26AS', 'ONLY_BOOKS', 'DIFF']

type Side = '26AS' | 'Books' | 'Diff'

/** What a row is, for selection purposes. DIFF is a residual within a matched
 *  party — explainable, never matchable. */
export function sideOf(status: string): Side {
  if (status === 'ONLY_26AS') return '26AS'
  if (status === 'ONLY_BOOKS') return 'Books'
  return 'Diff'
}

/** A row as the Explain modal wants it: what to call it and what it is worth,
 *  so the modal never has to fetch what the grid already has on screen. */
export type SelEntry = ExplainEntry & {
  side: Side
  /** the party these rows were grouped under, and the raw name on each side —
   *  what a cross-party selection needs in order to become an alias */
  party: string
  name26: string | null
  nameBk: string | null
}

function entryOf(it: DifferenceItem): SelEntry {
  const side = sideOf(it.status)
  const name = it.party_26as ?? it.party_books ?? it.party
  return {
    fingerprint: it.fingerprint!,
    side,
    label: `${side === 'Diff' ? 'Difference' : side} · ${name}`,
    amount: side === 'Books' ? it.tds_books : (it.tds_26as ?? it.tds_books),
    party: it.party,
    name26: it.party_26as ?? null,
    nameBk: it.party_books ?? null,
  }
}

export interface AliasOffer {
  names_26as: string[]
  names_books: string[]
  parties: string[]
}

/** What a selection spanning two parties should offer instead of a refusal.
 *
 *  Manual matching is restricted to one party, and rightly so — a match says
 *  "these entries are the same money", and money under two parties is two
 *  parties. But the reconciler who selected them is nearly always saying
 *  something else: that the two names are the same party. Without this the
 *  answer was a bare 422 naming the parties, with no way to act on it from
 *  the grid — the most common discovery in the reconciliation had no path out
 *  of the screen it was made on.
 *
 *  Returns null when there is nothing to offer: one party (the match is the
 *  right action), a Diff row in the selection (it is neither), or a side
 *  missing (an alias needs a name on each). */
export function aliasOffer(entries: SelEntry[]): AliasOffer | null {
  if (entries.length === 0 || entries.some((e) => e.side === 'Diff')) return null
  const parties = [...new Set(entries.map((e) => e.party))]
  if (parties.length < 2) return null
  const names_26as = [
    ...new Set(entries.filter((e) => e.side === '26AS').map((e) => e.name26).filter((n): n is string => !!n)),
  ]
  const names_books = [
    ...new Set(entries.filter((e) => e.side === 'Books').map((e) => e.nameBk).filter((n): n is string => !!n)),
  ]
  if (names_26as.length === 0 || names_books.length === 0) return null
  return { names_26as, names_books, parties: parties.sort() }
}

/** Sticky cells cover scrolled content, so they must be OPAQUE — the row's
 *  tint class rides on them too, and every tone here is a solid colour. */
function rowBgCls(it: DifferenceItem): string {
  if (it.status === 'USER_MATCH') return 'bg-violet-50'
  if (it.status === 'ALL_MATCH') return 'bg-background'
  if (it.status === 'DIFF') return 'bg-red-50'
  return 'bg-orange-50'
}

/** A totals row: label + the four money columns placed under their headers. */
function TotalsRow({ label, t, cls }: { label: string; t: DiffTotals; cls: string }) {
  const cell = (v: string) => (
    <td className="px-2 py-1.5 text-right font-bold">
      <Money v={v} bold className="text-xs" colorize={label.includes('cumulative') && Number(v) < 0} />
    </td>
  )
  return (
    <tr className={cls}>
      <td colSpan={6} className="px-2 py-1.5 font-bold">{label}</td>
      {cell(t.amt_26as)}
      {cell(t.tds_26as)}
      <td colSpan={5} />
      {cell(t.tds_books)}
      <td colSpan={3} />
      <td />
      {cell(t.tds_diff)}
      <td />
    </tr>
  )
}

/** One grid row, memoised. The grid is ~500 rows × 20 cells, and every tick of
 *  a checkbox (or a poll refetch that changed nothing) re-rendered all of them.
 *  With stable callbacks and TanStack's structural sharing keeping unchanged
 *  `it` references identical, only the row whose selection changed re-renders. */
const DiffRow = memo(function DiffRow({
  it,
  checked,
  tolerance,
  onToggle,
  onExplain,
  onUnmatch,
}: {
  it: DifferenceItem
  checked: boolean
  tolerance: number
  onToggle: (it: DifferenceItem) => void
  onExplain: (it: DifferenceItem) => void
  onUnmatch: (resolutionId: string) => void
}) {
  const bg = rowBgCls(it)
  const sm = STATUS_META[it.status]
  // DIFF rows are open work too — a residual inside a matched party. They
  // were selectable nowhere and explainable nowhere, which is why a party
  // could sit at "difference" indefinitely.
  const open_ = !!it.fingerprint && OPEN_STATUSES.includes(it.status)
  const pf = PARTY_META[it.party_flag]
  const rt = (v: string | null) => (
    <td className="px-2 py-1 text-right">
      {v ? <Money v={v} className="text-xs" /> : ''}
    </td>
  )
  return (
    <tr className={`border-b border-border ${bg}`}>
      <td style={stickyStyle(0)} className={`px-2 py-1 text-muted-foreground ${bg}`}>
        {open_ ? (
          <span className="flex flex-nowrap items-center gap-1">
            <input type="checkbox" checked={checked} onChange={() => onToggle(it)} />
            {it.serial_no}
          </span>
        ) : (
          it.serial_no
        )}
      </td>
      <td style={{ ...stickyStyle(1), maxWidth: 200 }} className={`px-2 py-1 ${bg}`}>
        <p className="truncate text-xs font-semibold">{it.party_26as ?? ''}</p>
      </td>
      <td className="px-2 py-1 font-mono text-[10px]">{it.pan ?? ''}</td>
      <td className="px-2 py-1 font-mono text-[10px]">{it.tan ?? ''}</td>
      <td className="px-2 py-1">{it.section ?? ''}</td>
      <td className="px-2 py-1">{it.date_26as ?? ''}</td>
      {rt(it.amt_26as)}
      {rt(it.tds_26as)}
      <td className="px-2 py-1" style={{ maxWidth: 190 }}>
        <p className="truncate text-xs">{it.party_books ?? ''}</p>
      </td>
      <td className="px-2 py-1 font-mono text-[10px]">{it.tan_books ?? ''}</td>
      <td className="px-2 py-1 text-muted-foreground">{it.section_books ?? ''}</td>
      <td className="px-2 py-1">{it.date_books ?? ''}</td>
      {rt(it.amt_books)}
      {rt(it.tds_books)}
      <td className="px-2 py-1">
        <Chip tone={DATE_TONE[it.date_flag] ?? 'gray'}>
          {it.date_flag === 'OUT_OF_WINDOW' ? 'out' : it.date_flag.toLowerCase()}
        </Chip>
      </td>
      <td className="px-2 py-1"><Chip tone={pf.tone}>{pf.label}</Chip></td>
      <td className="px-2 py-1">
        {it.sec_flag === 'MIXED'
          ? <Chip tone="violet">mixed</Chip>
          : <span className="text-muted-foreground">n/a</span>}
      </td>
      {rt(it.amt_diff)}
      <td className="px-2 py-1 text-right">
        {it.tds_diff ? <Money v={it.tds_diff} className="text-xs" colorize /> : ''}
      </td>
      <td className="px-2 py-1">
        <span className="flex flex-nowrap items-center gap-1">
          {it.match_residual &&
            Math.abs(Number(it.match_residual)) > tolerance && (
            <Chip tone="orange" title={`Matched as one group, but the two sides differ by ₹${Number(it.match_residual).toLocaleString('en-IN')} — a reason was recorded.`}>
              residual ₹{Number(it.match_residual).toLocaleString('en-IN')}
            </Chip>
          )}
          <Chip tone={sm.tone} solid={sm.solid}>{sm.label}</Chip>
          {it.confidence === 'LOW' &&
            (it.status === 'ALL_MATCH' || it.status === 'DIFF') && (
              <Chip tone="orange" title="Low-confidence match (aggregate net / weak corroboration) — worth verifying">
                low-conf
              </Chip>
            )}
          {it.status === 'USER_MATCH' && it.matched_by && (
            <Chip tone={it.match_pending ? 'gray' : 'violet'}
              title={`Matched by ${it.matched_by}${it.match_pending ? ' — pending approval' : ''}`}>
              {it.match_pending ? 'pending' : it.matched_by}
            </Chip>
          )}
          {it.status === 'USER_MATCH' && !it.match_pending &&
            it.match_resolution_id != null && (
              <Btn size="xs" kind="subtle" onClick={() => onUnmatch(it.match_resolution_id!)}>
                un-match
              </Btn>
            )}
          {/* the duplicate flag lived only on the Comparison sheet, so a party
              inflated by a double-posted voucher looked like any other
              unexplained difference. A CONFIRMED duplicate clears the entry;
              "not a duplicate" says the entry is real and leaves it open. */}
          {it.duplicate && (
            <Chip
              tone={it.duplicate_verdict === 'genuine' ? 'teal' : it.duplicate_verdict ? 'gray' : 'red'}
              title={
                it.duplicate_verdict === 'genuine'
                  ? 'Confirmed duplicate — this entry is the same money counted twice, and no longer counts as a difference'
                  : it.duplicate_verdict === 'not_duplicate'
                    ? 'Reviewed and kept: a real entry that happens to look identical'
                    : 'Identical to another entry in the same source — review it on the 26AS vs Books tab'
              }
            >
              {it.duplicate_verdict === 'genuine' ? 'duplicate'
                : it.duplicate_verdict ? 'not dup' : 'dup?'}
            </Chip>
          )}
          {/* per-row Explain, in the Status cell — the column profile is fixed
              at 20, so the affordance goes where the row's disposition lives */}
          {open_ && (
            <Btn size="xs" kind="subtle" onClick={() => onExplain(it)}
              title="Explain this entry — attach a reason and send it for review"
              className="text-primary">
              explain
            </Btn>
          )}
        </span>
      </td>
    </tr>
  )
})

function FiltersPopover({ nFilters, children }: { nFilters: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  return (
    <div className="relative" ref={ref}>
      <Btn size="xs" kind={nFilters ? 'primary' : 'default'} onClick={() => setOpen((s) => !s)}>
        <Filter size={14} /> Filters
        {nFilters ? (
          <span className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-background text-[10px] font-bold text-foreground">
            {nFilters}
          </span>
        ) : null}
      </Btn>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-60 space-y-2 rounded-md border border-border bg-background p-3 shadow-lg">
          {children}
        </div>
      )}
    </div>
  )
}

const selCls = 'w-full rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary'

/** The merged 26AS ↔ Books sheet: one row per matched pair or unmatched entry,
 *  the full 20-column profile, cumulative + grand-total footer on every page —
 *  and the place the work is actually done. One grid with a view selector, and
 *  the actions in it, because a row you can see and cannot act on is a row you
 *  have to go somewhere else to finish. */
export function DifferenceView({ runId, initialView = 'all' }: {
  runId: string | null
  initialView?: View
}) {
  const [view, setView] = useState<View>(initialView)
  const bucket = view === 'all' ? undefined : view
  const [status, setStatus] = useState<string | null>(null)
  const [dateFlag, setDateFlag] = useState<string | null>(null)
  const [partyFlag, setPartyFlag] = useState<string | null>(null)
  const [secFlag, setSecFlag] = useState<string | null>(null)
  const [party, setParty] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const debounced = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(50)
  const resetPage = () => setPage(1)
  const partiesQ = useDifferenceParties(runId, bucket, debounced)
  // fingerprint -> what it is, so we can require >=1 of each side before
  // matching. DIFF rows are selectable too: they cannot be MATCHED, but they
  // can be explained, and they were previously unreachable from this grid.
  const [sel, setSel] = useState<Map<string, SelEntry>>(new Map())
  const [reason, setReason] = useState('')
  const runQ = useRun(runId ?? '')
  const tolerance = Number(runQ.data?.tolerance ?? 1)
  const [explaining, setExplaining] = useState<ExplainEntry[] | null>(null)
  const queryClient = useQueryClient()

  //: clearing the selection whenever the visible set changes — otherwise an
  //: action applies to rows the user can no longer see, which is how a
  //: selection silently becomes a mistake
  const changeView = (next: View) => {
    setView(next)
    setStatus(null)
    setSel(new Map())
    setPage(1)
  }
  const refilter = <T,>(set: (v: T) => void) => (v: T) => {
    set(v)
    setSel(new Map())
    setPage(1)
  }

  //: stable across renders (functional updates, no captured state) so the
  //: memoised rows never re-render on account of the handlers themselves
  const toggle = useCallback((it: DifferenceItem) => {
    const entry = entryOf(it)
    setSel((m) => {
      const n = new Map(m)
      if (n.has(entry.fingerprint)) n.delete(entry.fingerprint)
      else n.set(entry.fingerprint, entry)
      return n
    })
  }, [])
  const explainOne = useCallback((it: DifferenceItem) => setExplaining([entryOf(it)]), [])

  const sides = new Set([...sel.values()].map((e) => e.side))
  const selected = [...sel.values()]
  const offer = aliasOffer(selected)
  // What the selection adds up to, before it is committed. A match asserts
  // that two sets of entries are the same money; the backend refuses one that
  // leaves a difference beyond tolerance without a reason, and the grid could
  // not supply a reason — so the correct action was impossible from the place
  // people take it, and the refusal arrived as a toast nobody read.
  const sum = (side: Side) =>
    selected.filter((e) => e.side === side)
      .reduce((a, e) => a + Number(e.amount ?? 0), 0)
  const residual = Math.round((sum('26AS') - sum('Books')) * 100) / 100
  const ties = Math.abs(residual) <= tolerance
  const needsReason = !ties && reason.trim().length === 0
  // one party, both sides, no Diff row. The party test used to live only in
  // the backend, so a cross-party selection was sent and came back a 422.
  const canMatch =
    sides.has('26AS') && sides.has('Books') && !sides.has('Diff') && offer === null

  const match = useMutation({
    mutationFn: () =>
      postJson<{ matched_by: string; pending: boolean; count: number }>(
        `/runs/${runId}/difference/manual-match`,
        { fingerprints: [...sel.keys()], comment: reason.trim() || undefined },
      ),
    onSuccess: (out) => {
      showToast({
        color: 'green',
        title: out.pending ? 'Match recorded — awaiting approval' : 'Rows matched',
        message: `${out.count} rows grouped by you${out.pending ? '; a reviewer must approve it' : ''}.`,
      })
      setSel(new Map())
      setReason('')
      for (const k of ['difference', 'difference-summary', 'difference-parties',
        'match-suggestions', 'reconciliation', 'cleared']) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
    },
    // `String(e)` renders "ApiError: …" with no title, and the message the
    // backend took care to word never reaches the reader.
    onError: (e) => showToast({
      color: 'red',
      title: 'Could not match these rows',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })

  const alias = useMutation({
    mutationFn: () => postJson(`/runs/${runId}/alias-groups`, offer),
    onSuccess: () => {
      showToast({
        color: 'green',
        title: 'Alias created',
        message: 'The names are one party from the next run. Past runs are unchanged.',
      })
      setSel(new Map())
      void queryClient.invalidateQueries({ queryKey: ['alias-groups'] })
      void queryClient.invalidateQueries({ queryKey: ['alias-suggestions'] })
      void queryClient.invalidateQueries({ queryKey: ['alias-candidates'] })
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not create the alias',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })

  const unmatch = useMutation({
    // reverse is one of the step-up-gated review verbs (guide §7.2)
    mutationFn: (resId: string) =>
      stepUpPost(`/resolutions/${resId}/reverse`, {
        comment: 'un-matched from reconciliation',
      }),
    onSuccess: () => {
      showToast({ color: 'blue', title: 'Match reversed — rows re-opened.' })
      for (const k of ['difference', 'difference-summary', 'difference-parties', 'match-suggestions', 'reconciliation']) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
    },
    onError: (e) => showToast({
      color: 'red',
      title: 'Could not reverse the match',
      message: e instanceof ApiError ? e.message : String(e),
    }),
  })

  const dataQ = useDifference(runId, {
    status: status ?? undefined,
    date_flag: dateFlag ?? undefined,
    party_flag: partyFlag ?? undefined,
    sec_flag: secFlag ?? undefined,
    party: party ?? undefined,
    bucket,
    search: debounced || undefined,
    page,
    size,
  })
  const data = dataQ.data
  const pan = data?.items.find((i) => i.pan)?.pan

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>
  const lastPage = Math.max(1, Math.ceil(data.total / size))
  // filters hidden in the popover, counted on its button — a hidden filter
  // must never silently narrow the table
  const nFilters = [status, dateFlag, partyFlag, secFlag].filter(Boolean).length

  const filterSelect = (label: string, value: string | null,
    set: (v: string | null) => void, options: { value: string; label: string }[]) => (
    <label className="block text-xs">
      <span className="mb-0.5 block font-medium text-foreground">{label}</span>
      <select className={selCls} value={value ?? ''}
        onChange={(e) => refilter(set)(e.target.value || null)}>
        <option value="">Any</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  )

  return (
    <div className="space-y-1.5">
      {/* the four cards became one slim chip strip — same figures, ~28px */}
      <ChipStrip>
        <StatChip label="TDS 26AS" value={data.totals.tds_26as} />
        <StatChip label="TDS books" value={data.totals.tds_books} />
        <StatChip
          label="Gap (26AS − books)"
          value={String(Number(data.totals.tds_26as) - Number(data.totals.tds_books))}
        />
        <StatChip label="Rows" value={data.total} />
      </ChipStrip>

      {/* ONE control band — view, party, a Filters popover holding the four
          flag selects (with an active count so a hidden filter can never
          silently narrow the table), search, and the pager docked at the
          right end of the same row. */}
      <div className="flex flex-nowrap items-center gap-2">
        <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-border text-xs">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              type="button"
              className={`px-2.5 py-1 ${view === v.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              onClick={() => changeView(v.value)}
            >
              {v.label}
            </button>
          ))}
        </span>
        {/* party AND status, together. They used to be mutually exclusive —
            whichever one you got depended on the tab — so a DIFF residual
            inside a chosen party could not be filtered to at all. */}
        <select
          className={`${selCls} w-48 shrink-0`}
          value={party ?? ''}
          onChange={(e) => refilter(setParty)(e.target.value || null)}
        >
          <option value="">All parties</option>
          {(partiesQ.data?.parties ?? []).map((p) => (
            <option key={p.party} value={p.party}>{p.party} ({p.n})</option>
          ))}
        </select>
        <FiltersPopover nFilters={nFilters}>
          {filterSelect('Status', status, setStatus,
            STATUSES_FOR[view].map((v) => ({ value: v, label: STATUS_META[v].label })))}
          {filterSelect('Date flag', dateFlag, setDateFlag,
            ['OK', 'OUT_OF_WINDOW', 'NA'].map((v) => ({ value: v, label: v })))}
          {filterSelect('Party flag', partyFlag, setPartyFlag,
            Object.keys(PARTY_META).map((v) => ({ value: v, label: v })))}
          {filterSelect('Section flag', secFlag, setSecFlag,
            ['NA', 'MIXED'].map((v) => ({ value: v, label: v })))}
        </FiltersPopover>
        <div className="flex w-44 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1">
          <Search size={13} className="shrink-0 text-muted-foreground" />
          <input
            className="w-full bg-transparent text-xs outline-none"
            placeholder="Search any column…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSel(new Map()); resetPage() }}
          />
        </div>
        {dataQ.isFetching && <span className="text-xs text-muted-foreground">…</span>}
        <div className="ml-auto">
          <TablePager total={data.total} page={page} size={size}
            onPage={setPage} onSize={setSize} dense />
        </div>
      </div>

      {sel.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md bg-violet-50 p-2">
          <p className="text-sm font-semibold text-foreground">
            {sel.size} row(s) selected — {sides.has('26AS') ? '26AS ✓' : '26AS —'} ·{' '}
            {sides.has('Books') ? 'Books ✓' : 'Books —'}
          </p>
          {canMatch && (
            <span className="flex flex-nowrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">difference</span>
              <span className={`font-mono text-sm font-bold ${ties ? 'text-emerald-700' : 'text-orange-700'}`}>
                ₹{residual.toLocaleString('en-IN')}
              </span>
              <Chip tone={ties ? 'green' : 'orange'}>
                {ties ? 'ties' : `beyond ₹${tolerance.toLocaleString('en-IN')}`}
              </Chip>
            </span>
          )}
          <Btn size="xs" kind="primary" disabled={!canMatch || needsReason}
            loading={match.isPending} onClick={() => match.mutate()}>
            <Link2 size={14} /> Match selected as a group
          </Btn>
          {/* the other half of Match/Explain, in the same place. A difference
              that is real cannot be matched away, and having to leave the grid
              to say so is why one-sided entries went unaddressed. */}
          <Btn size="xs" onClick={() => setExplaining(selected)}>
            <MessageSquare size={14} /> Explain selected
          </Btn>
          <Btn size="xs" kind="subtle" onClick={() => setSel(new Map())}>Clear</Btn>
          {offer && (
            <Btn size="xs" loading={alias.isPending} onClick={() => alias.mutate()}
              className="border-violet-300 text-violet-700">
              <Tags size={14} /> Same party — create alias
            </Btn>
          )}
          {canMatch && !ties && (
            /* the reason the backend requires, asked for where the judgement
               is being made rather than reported as a refusal afterwards */
            <input
              className="w-80 rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why the amounts differ — short deduction, rounding, part payment…"
            />
          )}
          {!canMatch && (
            <p className="text-xs text-muted-foreground">
              {offer
                ? `These rows are under ${offer.parties.length} parties (${offer.parties.join(', ')}), so they are not one match — but they can be one PARTY.`
                : sides.has('Diff')
                  ? 'A difference row can be explained, not matched.'
                  : 'Matching needs at least one Only-26AS and one Only-Books row.'}
            </p>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full border-collapse text-xs" style={{ minWidth: MIN_WIDTH }}>
          <thead>
            <tr>
              {COLS.map(([label, w], i) => (
                <th
                  key={i}
                  className="border-b border-r border-border bg-muted px-2 py-1.5 text-left font-medium text-muted-foreground"
                  style={{ minWidth: w, ...(i < 2 ? { ...stickyStyle(i), zIndex: 3 } : undefined) }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <DiffRow
                key={it.id}
                it={it}
                checked={!!it.fingerprint && sel.has(it.fingerprint)}
                tolerance={tolerance}
                onToggle={toggle}
                onExplain={explainOne}
                onUnmatch={unmatch.mutate}
              />
            ))}
          </tbody>
          <tfoot>
            <TotalsRow
              label={`Cumulative through page ${page} of ${lastPage}`}
              t={data.cumulative}
              cls="bg-blue-50"
            />
            <TotalsRow
              label={`Grand total — all ${fmt.format(data.total)} row(s) in this filter`}
              t={data.totals}
              cls="bg-muted"
            />
          </tfoot>
        </table>
      </div>

      <TablePager total={data.total} page={page} size={size} onPage={setPage} onSize={setSize} compact />

      {explaining && runId != null && (
        <ExplainModal
          runId={runId}
          entries={explaining}
          onClose={() => { setExplaining(null); setSel(new Map()) }}
        />
      )}

      <p className="rounded-md bg-muted p-2.5 text-xs text-foreground">
        One row per matched pair (or unmatched entry); an <b>aggregate n:1</b> row means the
        party reconciles as a whole (books booked as one journal vs itemised 26AS). The bottom
        two rows show the <b>running cumulative</b> through this page and the <b>grand total</b>
        {' '}of the whole filter. Data limits: <b>Section Books</b> / <b>Amt Books</b> are blank (the
        books ledger carries no per-line section or gross amount); <b>PAN</b> is the assessee&apos;s,
        {pan ? ` ${pan},` : ''} not the deductor&apos;s — <b>TAN</b> is the cross-reference;
        matching is on <b>TDS</b>. <b>TAN✓ name≠</b> = joined by TAN though the books use a
        different name.
      </p>
    </div>
  )
}
